import type { DealTransformed } from "@/lib/schema/deal-schema";
import { tearsheetProgress } from "@/lib/tearsheet-stages";
import { lazy, Suspense, useState } from "react";
import { Loader2, X } from "lucide-react";
import { useWorkspaceStore } from "@/store/workspace-store";
import { getReportItem } from "@/components/deal-detail/report-types";
import {
  OPTIMISTIC_VERSION_ID,
  useReportPanel,
} from "@/hooks/use-report-panel";
import {
  ReportPanelToolbar,
  type PdfAction,
  type PanelToolbarActions,
} from "@/components/deal-detail/report-panel-toolbar";
import { useDealStatsStore } from "@/store/deal-stats-store";
import { PremiumActivationDialog } from "@/components/modals/premium-activation-dialog";

// Lazy load report components
const TearSheetComponent = lazy(() =>
  import("@/components/reports/tearsheet-v2").then((m) => ({
    default: m.TearsheetV2,
  }))
);
const DealBriefComponent = lazy(() =>
  import("@/components/reports/deal-brief").then((m) => ({
    default: m.DealBriefComponent,
  }))
);
const DealMemoComponent = lazy(() =>
  import("@/components/reports/deal-memo").then((m) => ({
    default: m.default,
  }))
);
const MetricsComponent = lazy(() =>
  import("@/components/reports/metrics").then((m) => ({
    default: m.default,
  }))
);
const DealFitComponent = lazy(() =>
  import("@/components/reports/deal-fit").then((m) => ({
    default: m.DealFitReportComponent,
  }))
);
const GapAnalysisComponent = lazy(() =>
  import("@/components/reports/gap-analysis").then((m) => ({
    default: m.GapAnalysisComponent,
  }))
);
const InsightsComponent = lazy(() =>
  import("@/components/reports/insights").then((m) => ({
    default: m.default,
  }))
);
const MarketIntelComponent = lazy(() =>
  import("@/components/reports/market-intelligence").then((m) => ({
    default: m.default,
  }))
);
const StartupScoreComponent = lazy(() =>
  import("@/components/reports/startupscore").then((m) => ({
    default: m.StartupScoreComponent,
  }))
);
const ValuationsComponent = lazy(() =>
  import("@/components/reports/valuations").then((m) => ({
    default: m.ValuationsComponent,
  }))
);

// ---------- Source-data panels (info pages from the legacy sidebar) ----------
// All four accept `currentDeal`; Portco additionally needs the active
// workspace id and company id, so we wrap it in a small adapter that
// pulls those from the workspace store + currentDeal at mount time.
// QuestionnaireComponent expects an `isLoading` prop the panel doesn't
// have a way to pass — by the time the user opens this from the
// Analysis tab the deal is already loaded, so we hard-wire `false`.
const QuestionnairePanel = lazy(() =>
  import("@/components/deal-detail/data-lake/questionnaire").then((m) => ({
    default: ({
      currentDeal,
      onPanelActionsChange,
      onPanelPdfChange,
    }: {
      currentDeal: DealTransformed;
      onPanelActionsChange?: (actions: PanelToolbarActions | null) => void;
      onPanelPdfChange?: (actions: PdfAction[] | null) => void;
    }) => (
      <m.QuestionnaireComponent
        currentDeal={currentDeal}
        isLoading={false}
        isPanelMode
        onPanelActionsChange={onPanelActionsChange}
        onPanelPdfChange={onPanelPdfChange}
      />
    ),
  }))
);
const WebsiteInfoPanel = lazy(() =>
  import("@/components/deal-detail/data-lake/website-info-v2").then((m) => ({
    default: ({
      currentDeal,
      onPanelPdfChange,
    }: {
      currentDeal: DealTransformed;
      onPanelPdfChange?: (actions: PdfAction[] | null) => void;
    }) => (
      <m.WebsiteInfoV2Component
        currentDeal={currentDeal}
        isPanelMode
        onPanelPdfChange={onPanelPdfChange}
      />
    ),
  }))
);
const LinkedInInfoPanel = lazy(() =>
  import("@/components/deal-detail/data-lake/linkedin-info").then((m) => ({
    default: ({
      currentDeal,
      initialVersionId,
      onPanelPdfChange,
    }: {
      currentDeal: DealTransformed;
      initialVersionId?: string;
      onPanelPdfChange?: (actions: PdfAction[] | null) => void;
    }) => (
      <m.LinkedInInfoComponent
        currentDeal={currentDeal}
        isPanelMode
        initialVersionId={initialVersionId}
        onPanelPdfChange={onPanelPdfChange}
      />
    ),
  }))
);

const componentMap: Record<
  string,
  React.LazyExoticComponent<
    React.ComponentType<{
      currentDeal: DealTransformed;
      initialVersionId?: string;
      /** Panel mounts the component with `isPanelMode`; legacy routes don't. */
      isPanelMode?: boolean;
      /**
       * Panel-mode components call this to register (or clear, with null) the
       * PDF export actions that the shared toolbar should surface. Legacy
       * routes don't pass this, so the component falls back to its inline PDF
       * UI (when one exists).
       */
      onPanelPdfChange?: (actions: PdfAction[] | null) => void;
      /**
       * Panel-mode components call this to register (or clear, with null) their
       * toolbar buttons (e.g. valuations' Generate + Pause/Reset) on the shared
       * toolbar. Legacy routes don't pass it and keep their inline controls.
       */
      onPanelActionsChange?: (actions: PanelToolbarActions | null) => void;
      /**
       * Panel-mode components call this to jump the deal page to another tab
       * (e.g. gap-analysis' "Deal Info" source → Profile). Routes the request
       * through the page's tab handler, which also closes this panel. Legacy
       * routes don't pass it.
       */
      onPanelNavigateToTab?: (tab: string, targetId?: string) => void;
    }>
  >
> = {
  "tear-sheet": TearSheetComponent,
  "deal-brief": DealBriefComponent,
  "deal-memo": DealMemoComponent,
  metrics: MetricsComponent,
  "deal-fit": DealFitComponent,
  "gap-analysis": GapAnalysisComponent,
  insights: InsightsComponent,
  "market-intel": MarketIntelComponent,
  "business-risk": StartupScoreComponent,
  valuations: ValuationsComponent,
  questionnaire: QuestionnairePanel,
  "website-info": WebsiteInfoPanel,
  "linkedin-info": LinkedInInfoPanel,
};

interface ReportPanelViewerProps {
  type: string;
  title: string;
  versionId?: string;
  currentDeal: DealTransformed;
  reportStatus?: unknown;
  maximized?: boolean;
  onToggleMaximize?: () => void;
  onClose: () => void;
  onNavigateToTab?: (tab: string, targetId?: string) => void;
}

export function ReportPanelViewer({
  type,
  title,
  versionId,
  currentDeal,
  reportStatus,
  maximized,
  onToggleMaximize,
  onClose,
  onNavigateToTab,
}: ReportPanelViewerProps) {
  const Component = componentMap[type];
  const item = getReportItem(type);
  const { activeWorkspaceId } = useWorkspaceStore();

  if (!Component || !item) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between border-b border-border bg-card px-4 py-2.5">
          <span className="text-xs font-medium text-muted-foreground">
            {title}
          </span>
          <button
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
          Report type not found
        </div>
      </div>
    );
  }

  return (
    <PanelInner
      item={item}
      Component={Component}
      title={title}
      versionId={versionId}
      currentDeal={currentDeal}
      reportStatus={reportStatus}
      workspaceId={activeWorkspaceId}
      maximized={maximized}
      onToggleMaximize={onToggleMaximize}
      onClose={onClose}
      onNavigateToTab={onNavigateToTab}
    />
  );
}

interface PanelInnerProps {
  item: NonNullable<ReturnType<typeof getReportItem>>;
  Component: (typeof componentMap)[string];
  title: string;
  versionId?: string;
  currentDeal: DealTransformed;
  reportStatus: unknown;
  workspaceId: string | null;
  maximized?: boolean;
  onToggleMaximize?: () => void;
  onClose: () => void;
  onNavigateToTab?: (tab: string, targetId?: string) => void;
}

function PanelInner({
  item,
  Component,
  title,
  versionId,
  currentDeal,
  reportStatus,
  workspaceId,
  maximized,
  onToggleMaximize,
  onClose,
  onNavigateToTab,
}: PanelInnerProps) {
  const panel = useReportPanel({
    item,
    currentDeal,
    workspaceId,
    reportStatus,
    initialVersionId: versionId,
    onClose,
  });

  const [pdfActions, setPdfActions] = useState<PdfAction[] | null>(null);
  const [panelActions, setPanelActions] =
    useState<PanelToolbarActions | null>(null);
  const { dealStats } = useDealStatsStore();
  const [confirmGenerate, setConfirmGenerate] = useState(false);

  // Generating a premium report on a non-premium deal must go through the
  // mark-premium confirmation (SAAS-798) — the same gate as Run/Regenerate in
  // the report list, so the toolbar's "New version" can't silently consume a
  // premium slot.
  const needsActivationToGenerate =
    item.requiresActivation && currentDeal.aiTracked !== true;
  const handleGenerateNew = () => {
    if (needsActivationToGenerate) setConfirmGenerate(true);
    else panel.generateNew();
  };

  const showOptimisticPlaceholder =
    panel.selectedVersionId === OPTIMISTIC_VERSION_ID;
  const optimisticNumber = panel.optimistic?.versionNumber;
  const tearsheetStageLabel = tearsheetProgress(
    (reportStatus as { opportunityApplication?: { report_status?: { tearsheet?: string } } })
      ?.opportunityApplication?.report_status?.tearsheet
  )?.label;

  return (
    <div className="flex h-full flex-col overflow-hidden bg-muted/20">
      <ReportPanelToolbar
        title={title}
        versions={panel.versions}
        selectedVersionId={panel.selectedVersionId}
        onVersionChange={panel.setSelectedVersionId}
        hideVersionSelector={item.skipVersions}
        hideGenerate={item.skipGenerate}
        hideDelete={item.skipDelete}
        canGenerate={panel.canGenerate}
        canDelete={panel.canDelete}
        isGenerating={panel.isGenerating}
        isDeleting={panel.isDeleting}
        onGenerateNew={handleGenerateNew}
        onDelete={panel.deleteSelected}
        pdfActions={pdfActions ?? undefined}
        primaryAction={panelActions?.primaryAction}
        secondaryActions={panelActions?.secondaryActions}
        maximized={maximized}
        onToggleMaximize={onToggleMaximize}
        onClose={onClose}
        hasError={panel.hasError}
        onRetry={() => {
          panel.clearError();
          handleGenerateNew();
        }}
        onDismissError={panel.clearError}
      />

      {/*
        Tearsheet renders its own full-height layout with a pinned
        composer footer; other reports use the shared scroll + card
        chrome. Giving tearsheet direct control of the flex column
        lets `position: sticky` / flex-footer layouts actually reach
        the panel's bottom edge instead of being clipped by the
        overflow-hidden rounded card.
      */}
      {item.id === "tear-sheet" ? (
        <div className="flex-1 flex min-h-0 overflow-hidden">
          {showOptimisticPlaceholder ? (
            <div className="flex w-full flex-col items-center gap-3 p-12 text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Generating v{optimisticNumber}…</span>
              {tearsheetStageLabel && (
                <span className="text-foreground/80">{tearsheetStageLabel}</span>
              )}
              <span className="text-xs">
                Select a previous version from the dropdown to view it while
                this completes.
              </span>
            </div>
          ) : (
            <Suspense
              fallback={
                <div className="flex h-64 w-full items-center justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              }
            >
              <Component
                key={panel.selectedVersionId || "default"}
                currentDeal={currentDeal}
                initialVersionId={panel.selectedVersionId || undefined}
                isPanelMode
                onPanelPdfChange={setPdfActions}
                onPanelActionsChange={setPanelActions}
                onPanelNavigateToTab={onNavigateToTab}
              />
            </Suspense>
          )}
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto bg-card p-4">
          {showOptimisticPlaceholder ? (
            <div className="flex flex-col items-center gap-3 p-12 text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Generating v{optimisticNumber}…</span>
              <span className="text-xs">
                Select a previous version from the dropdown to view it while
                this completes.
              </span>
            </div>
          ) : (
            <Suspense
              fallback={
                <div className="flex h-64 items-center justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              }
            >
              <Component
                key={panel.selectedVersionId || "default"}
                currentDeal={currentDeal}
                initialVersionId={panel.selectedVersionId || undefined}
                isPanelMode
                onPanelPdfChange={setPdfActions}
                onPanelActionsChange={setPanelActions}
                onPanelNavigateToTab={onNavigateToTab}
              />
            </Suspense>
          )}
        </div>
      )}

      <PremiumActivationDialog
        open={confirmGenerate}
        onClose={() => setConfirmGenerate(false)}
        onConfirm={() => {
          setConfirmGenerate(false);
          panel.generateNew();
        }}
        reportName={title}
        nextStage={null}
        currentTracked={dealStats?.tracked ?? 0}
        showStatusChange={false}
      />
    </div>
  );
}
